import { AadeBookInvoiceType } from '../../interfaces/myData.interface';
import {
  CONFIRM_DELIVERY_OUTCOME,
  DELIVERY_NOTE_STATUS_CODE,
  DeliveryNoteLifecycleCode,
  DeliveryNoteStatus,
  PackagingType,
  TransportType,
} from '../../interfaces/myDataDelivery.interface';

/**
 * myDATA doesn't register a transit action (RegisterTransfer/ConfirmDeliveryOutcome/RejectDeliveryNote)
 * synchronously on their end -- calling GetDeliveryNoteStatus right after a successful action can still
 * return the pre-action status/lifecycle. Give it a few seconds before re-fetching.
 */
export const TRANSIT_STATUS_REFRESH_DELAY_MS = 1_500;

/**
 * Helper to map TransportType enum values to a simple select-friendly array.
 * Useful for building a static select input for transportType in forms.
 */
export const getTransportTypeOptions = () => {
  // Using numeric enum values as defined in `TransportType`.
  // The UI/text mapping is handled elsewhere via i18n keys (see DeliveryStatusStep usage).
  return [
    { value: TransportType.PUBLIC_USE_TRUCK, label: 'DELIVERY.TRANSPORT_TYPE_PUBLIC_USE_TRUCK' },
    { value: TransportType.PRIVATE_USE_TRUCK, label: 'DELIVERY.TRANSPORT_TYPE_PRIVATE_USE_TRUCK' },
    { value: TransportType.SHIP, label: 'DELIVERY.TRANSPORT_TYPE_SHIP' },
    { value: TransportType.TRAIN, label: 'DELIVERY.TRANSPORT_TYPE_TRAIN' },
    { value: TransportType.AIRPLANE, label: 'DELIVERY.TRANSPORT_TYPE_AIRPLANE' },
    { value: TransportType.OTHER, label: 'DELIVERY.TRANSPORT_TYPE_OTHER' },
    { value: TransportType.NONE, label: 'DELIVERY.TRANSPORT_TYPE_NONE' },
  ];
};

export const getDeliveryOutcomeOptions = (isUserCounterpart?: boolean) => {
  if (isUserCounterpart) {
    return [
      { value: CONFIRM_DELIVERY_OUTCOME.FULL, label: 'DELIVERY.OUTCOME_FULL' },
      { value: CONFIRM_DELIVERY_OUTCOME.PARTIAL, label: 'DELIVERY.OUTCOME_PARTIAL' },
    ];
  }

  return [
    { value: CONFIRM_DELIVERY_OUTCOME.FULL, label: 'DELIVERY.OUTCOME_FULL' },
    { value: CONFIRM_DELIVERY_OUTCOME.PARTIAL, label: 'DELIVERY.OUTCOME_PARTIAL' },
    { value: CONFIRM_DELIVERY_OUTCOME.NONE, label: 'DELIVERY.OUTCOME_NONE' },
  ];
};

/**
 * Helper to map PackagingType enum values to a simple select-friendly array.
 * Useful for building a static select input for packagingType in forms.
 */
export const getPackagingTypeOptions = () => {
  return [
    { value: PackagingType.PALLET, label: 'DELIVERY.PACKAGING_TYPE_PALLET' },
    { value: PackagingType.BOX, label: 'DELIVERY.PACKAGING_TYPE_BOX' },
    { value: PackagingType.CRATE, label: 'DELIVERY.PACKAGING_TYPE_CRATE' },
    { value: PackagingType.BARREL, label: 'DELIVERY.PACKAGING_TYPE_BARREL' },
    { value: PackagingType.SACK, label: 'DELIVERY.PACKAGING_TYPE_SACK' },
    { value: PackagingType.OTHER, label: 'DELIVERY.PACKAGING_TYPE_OTHER' },
  ];
};

/**
 * Maps the doc-embedded `DeliveryNoteLifecycleCode` (numeric) to the equivalent live
 * `DELIVERY_NOTE_STATUS_CODE` (string) returned by `GetDeliveryNoteStatus`, so callers can compare
 * against a single set of codes regardless of which source the status came from. There's no
 * `DELIVERY_NOTE_STATUS_CODE` equivalent for `IN_TRANSIT_RETURN`.
 */
const LIFECYCLE_TO_STATUS_CODE: Partial<Record<DeliveryNoteLifecycleCode, DELIVERY_NOTE_STATUS_CODE>> = {
  [DeliveryNoteLifecycleCode.REGISTERED]: DELIVERY_NOTE_STATUS_CODE.REGISTERED,
  [DeliveryNoteLifecycleCode.CANCELLED]: DELIVERY_NOTE_STATUS_CODE.CANCELLED,
  [DeliveryNoteLifecycleCode.IN_TRANSIT]: DELIVERY_NOTE_STATUS_CODE.IN_TRANSIT,
  [DeliveryNoteLifecycleCode.REJECTED]: DELIVERY_NOTE_STATUS_CODE.REJECTED,
  [DeliveryNoteLifecycleCode.DELIVERED_BY_CARRIER]: DELIVERY_NOTE_STATUS_CODE.DELIVERED_BY_CARRIER,
  [DeliveryNoteLifecycleCode.FAILED_DELIVERY]: DELIVERY_NOTE_STATUS_CODE.FAILED_DELIVERY,
  [DeliveryNoteLifecycleCode.COMPLETED]: DELIVERY_NOTE_STATUS_CODE.COMPLETED,
};

/**
 * Prefers the live `GetDeliveryNoteStatus` status (now available to issuer and recipient alike),
 * falling back to the doc's own embedded `invoiceDeliveryStatus` when the live call didn't return
 * one (e.g. it's known to be omitted while the doc is still REGISTERED).
 */
const resolveStatusCode = (
  docStatus?: DeliveryNoteStatus,
  invoice?: AadeBookInvoiceType | null,
): DELIVERY_NOTE_STATUS_CODE | undefined => {
  if (docStatus?.status) return docStatus.status;
  if (invoice?.invoiceDeliveryStatus) return LIFECYCLE_TO_STATUS_CODE[invoice.invoiceDeliveryStatus];
  return undefined;
};

/**
 * Returns true when the delivery status allows registering a transfer.
 * According to the business rules this is allowed when the delivery note status is
 * either REGISTERED or IN_TRANSIT. This rule applies the same regardless of who's calling.
 *
 * In case the status can't be resolved at all (e.g. a carrier without doc access) we allow the
 * action since we don't know the current status of the doc in order to restrict it.
 */
export const canRegisterTransfer = (docStatus?: DeliveryNoteStatus, invoice?: AadeBookInvoiceType | null): boolean => {
  const code = resolveStatusCode(docStatus, invoice);
  if (code) return code === DELIVERY_NOTE_STATUS_CODE.REGISTERED || code === DELIVERY_NOTE_STATUS_CODE.IN_TRANSIT;

  return true;
};

/**
 * Returns true when the delivery status allows confirming an outcome. The issuer may only do so
 * while IN_TRANSIT; the recipient (counterpart) may also do so once the carrier has already
 * declared delivery (DELIVERED_BY_CARRIER), since that's exactly the confirmation they're making.
 *
 * `isIssuer` is undefined when the role can't be resolved (e.g. a carrier without doc access) --
 * in that case we allow the action only if the status is unknown too, matching the previous
 * "we don't know enough to restrict it" fallback.
 */
export const canConfirmOutcome = (
  isIssuer: boolean | undefined,
  docStatus?: DeliveryNoteStatus,
  invoice?: AadeBookInvoiceType | null,
): boolean => {
  const code = resolveStatusCode(docStatus, invoice);
  if (!code) return isIssuer === undefined;

  if (isIssuer) return code === DELIVERY_NOTE_STATUS_CODE.IN_TRANSIT;

  return code === DELIVERY_NOTE_STATUS_CODE.IN_TRANSIT || code === DELIVERY_NOTE_STATUS_CODE.DELIVERED_BY_CARRIER;
};

export const getConfirmOutcomeButtonCopy = (isIssuer: boolean | undefined): string => {
  if (isIssuer) return 'DELIVERY_BUTTON';

  if (isIssuer === false) return 'RECEIVE_BUTTON';

  return 'DELIVERY_RECEIVE_BUTTON';
};

export const isUserCounterpart = (isIssuer: boolean | undefined): boolean => isIssuer === false;

/**
 * The issuer can never `RegisterRejection`. The recipient (counterpart) can, while the doc is
 * IN_TRANSIT or already DELIVERED_BY_CARRIER.
 *
 * `isIssuer` is undefined when the role can't be resolved (e.g. a carrier without doc access) --
 * in that case we allow the action only if the status is unknown too, matching the previous
 * "we don't know enough to restrict it" fallback.
 */
export const canRegisterRejection = (
  isIssuer: boolean | undefined,
  docStatus?: DeliveryNoteStatus,
  invoice?: AadeBookInvoiceType | null,
): boolean => {
  if (isIssuer) return false;

  const code = resolveStatusCode(docStatus, invoice);
  if (code) return code === DELIVERY_NOTE_STATUS_CODE.IN_TRANSIT || code === DELIVERY_NOTE_STATUS_CODE.DELIVERED_BY_CARRIER;

  return isIssuer === undefined;
};

/**
 * Only the issuer can `ConfirmDeliveryReturn`, closing out a delivery note whose goods are coming
 * back undelivered (rejected, partially delivered, or a failed delivery attempt).
 *
 * Note: myDATA also allows this call while IN_TRANSIT for two doc-type-specific cases (aggregate/
 * type-9.2 delivery notes, and type-9.3 reverse-delivery-notes with `reverseDeliveryNote=true`) --
 * neither is modeled here, since there's no doc-type/`reverseDeliveryNote` indicator on our types
 * yet (see CHANGELOG "myDATA v2.0.2 — Pending Implementation").
 *
 * `isIssuer` is undefined when the role can't be resolved (e.g. a carrier without doc access) --
 * in that case we allow the action only if the status is unknown too, matching the previous
 * "we don't know enough to restrict it" fallback.
 */
export const canConfirmDeliveryReturn = (
  isIssuer: boolean | undefined,
  docStatus?: DeliveryNoteStatus,
  invoice?: AadeBookInvoiceType | null,
): boolean => {
  if (isIssuer === false) return false;

  const code = resolveStatusCode(docStatus, invoice);
  if (code) {
    return (
      code === DELIVERY_NOTE_STATUS_CODE.REJECTED ||
      code === DELIVERY_NOTE_STATUS_CODE.DELIVERED_BY_CARRIER ||
      code === DELIVERY_NOTE_STATUS_CODE.FAILED_DELIVERY
    );
  }

  return isIssuer === undefined;
};
